import { c as _c } from "react/compiler-runtime";
import { CurrencyEnum, DateRangePicker, FormInputSearch, FormSelect, Hint, PrimaryButton, cents, centsToUnits, toast, units } from '@systemeio/ui';
import { AFFILIATE_PROGRAM_API, updateCommission } from 'modules/affiliate-program/api/affiliate-program-api';
import { AffiliateCommissionInput, AffiliatePayoutInput } from 'modules/affiliate-program/components/affiliate-input';
import AffiliateStatisticSkeleton from 'modules/affiliate-program/components/affiliate-statistic-skeleton';
import { AffiliateProgramLoco } from 'modules/affiliate-program/constants/affiliate-program-loco';
import { useAffiliateStatistic } from 'modules/affiliate-program/hooks/use-affiliate-statistic';
import { useAffiliates } from 'modules/affiliate-program/hooks/use-affiliates';
import { useCommissions } from 'modules/affiliate-program/hooks/use-commissions';
import { AffiliateFilterInterface, AffiliateInterface, CommissionInterface } from 'modules/affiliate-program/types/affiliate-program-interface';
import { getAffiliateSum } from 'modules/affiliate-program/utils/get-affiliate-sum';
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import { useFilterWithDate } from 'shared/route-query/hooks/use-filter/use-filter-with-date';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { currencyFormat } from 'shared/utils/currency-format';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getCurrencySymbol } from 'shared/utils/get-currency-symbol';
import { getThisMonthDateRange, getToday } from 'shared/utils/get-dates';
import { SettingsIcon } from '@systemeio/ui/icons';
const defaultFilters: AffiliateFilterInterface = {
  email: '',
  id: '',
  dateSince: null,
  dateTill: null
};
const AffiliateProgram: NextPage = () => {
  const $ = _c(271);
  const {
    filter: affiliatesFilter,
    setFilter: setAffiliatesFilter
  } = useFilterWithDate(defaultFilters, getThisMonthDateRange);
  const {
    setPagination: setPaginationAffiliates,
    pagination: paginationAffiliates,
    setLimit: setLimitAffiliates,
    limit: limitAffiliates
  } = usePagination(true);
  const {
    setPagination: setPaginationCommissions,
    pagination: paginationCommissions,
    setLimit: setLimitCommissions,
    limit: limitCommissions
  } = usePagination(true);
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const [selectedCurrency, setSelectedCurrency] = useState(undefined);
  const [currencyData, setCurrencyData] = useState();
  let t0;
  if ($[0] !== affiliatesFilter || $[1] !== limitAffiliates || $[2] !== paginationAffiliates) {
    t0 = {
      limit: limitAffiliates,
      pagination: paginationAffiliates,
      filter: affiliatesFilter
    };
    $[0] = affiliatesFilter;
    $[1] = limitAffiliates;
    $[2] = paginationAffiliates;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    affiliates,
    affiliatesCounter
  } = useAffiliates(t0);
  let t1;
  if ($[4] !== limitCommissions || $[5] !== paginationCommissions) {
    t1 = {
      limit: limitCommissions,
      pagination: paginationCommissions
    };
    $[4] = limitCommissions;
    $[5] = paginationCommissions;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    commissions,
    mutateCommissions,
    commissionsCounter
  } = useCommissions(t1);
  let t2;
  if ($[7] !== affiliatesFilter) {
    t2 = {
      filter: affiliatesFilter
    };
    $[7] = affiliatesFilter;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    affiliateStatistic
  } = useAffiliateStatistic(t2);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      api: `${AFFILIATE_PROGRAM_API}/affiliate-commissions`
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const updateCommissionData = useUpdateData(t3);
  let t4;
  if ($[10] !== affiliatesFilter.dateSince || $[11] !== setAffiliatesFilter || $[12] !== user) {
    t4 = () => {
      if (user && affiliatesFilter.dateSince === null) {
        const [defaultDateSince, defaultDateTill] = getThisMonthDateRange(user.timezone);
        setAffiliatesFilter(prev => ({
          ...prev,
          dateSince: defaultDateSince,
          dateTill: defaultDateTill
        }));
      }
    };
    $[10] = affiliatesFilter.dateSince;
    $[11] = setAffiliatesFilter;
    $[12] = user;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== affiliatesFilter || $[15] !== setAffiliatesFilter || $[16] !== user) {
    t5 = [user, affiliatesFilter, setAffiliatesFilter];
    $[14] = affiliatesFilter;
    $[15] = setAffiliatesFilter;
    $[16] = user;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== affiliateStatistic || $[19] !== intlLocale || $[20] !== user) {
    t6 = () => {
      if (affiliateStatistic && !Array.isArray(affiliateStatistic.commonAffiliateSalesInfo) && user) {
        const currencies = Object.keys(affiliateStatistic.commonAffiliateSalesInfo) as CurrencyEnum[];
        setCurrencyData(currencies.map(el => ({
          id: el,
          caption: getCurrencySymbol(el, intlLocale)
        })));
        setSelectedCurrency(currencies.includes(CurrencyEnum.EURO) ? CurrencyEnum.EURO : currencies[0]);
      } else {
        setCurrencyData(undefined);
        setSelectedCurrency(undefined);
      }
    };
    $[18] = affiliateStatistic;
    $[19] = intlLocale;
    $[20] = user;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== affiliateStatistic || $[23] !== intlLocale) {
    t7 = [affiliateStatistic, intlLocale];
    $[22] = affiliateStatistic;
    $[23] = intlLocale;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  useEffect(t6, t7);
  const [isPayoutFetching, setIsPayoutFetching] = useState(false);
  const [isCommissionFetching, setIsCommissionFetching] = useState(false);
  let t8;
  if ($[25] !== commissions || $[26] !== mutateCommissions || $[27] !== t || $[28] !== updateCommissionData) {
    t8 = async (commission, value) => {
      ;
      try {
        setIsPayoutFetching(true);
        await updateCommission(commission.id, {
          affiliateCommission: commission.affiliateCommission,
          payoutDelay: value
        });
        await updateCommissionData(false, undefined, {
          mutationData: commissions,
          onMutate: async () => {
            await mutateCommissions(data => {
              if (data) {
                return {
                  items: data.items.map(item => {
                    if (item.id === commission.id) {
                      item = {
                        ...item,
                        payoutDelay: value
                      };
                    }
                    return item;
                  }),
                  hasMore: data.hasMore
                };
              }
            }, false);
          }
        });
        setIsPayoutFetching(false);
        toast.success(t("dashboard.affiliate_program.payout_delay_changed"));
      } catch (t9) {
        setIsPayoutFetching(false);
        toast.error(t("global.error"));
      }
    };
    $[25] = commissions;
    $[26] = mutateCommissions;
    $[27] = t;
    $[28] = updateCommissionData;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const onSavePayoutDelay = t8;
  let t9;
  if ($[30] !== commissions || $[31] !== mutateCommissions || $[32] !== t || $[33] !== updateCommissionData) {
    t9 = async (commission_0, value_0) => {
      ;
      try {
        setIsCommissionFetching(true);
        await updateCommission(commission_0.id, {
          payoutDelay: commission_0.payoutDelay,
          affiliateCommission: value_0
        });
        await updateCommissionData(false, undefined, {
          mutationData: commissions,
          onMutate: async () => {
            await mutateCommissions(data_0 => {
              if (data_0) {
                return {
                  items: data_0.items.map(item_0 => {
                    if (item_0.id === commission_0.id) {
                      item_0 = {
                        ...item_0,
                        affiliateCommission: value_0
                      };
                    }
                    return item_0;
                  }),
                  hasMore: data_0.hasMore
                };
              }
            }, false);
          }
        });
        setIsCommissionFetching(false);
        toast.success(t("dashboard.affiliate_program.affiliate_commission_changed"));
      } catch (t10) {
        setIsCommissionFetching(false);
        toast.error(t("global.error"));
      }
    };
    $[30] = commissions;
    $[31] = mutateCommissions;
    $[32] = t;
    $[33] = updateCommissionData;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const onSaveCommission = t9;
  let t10;
  if ($[35] !== t) {
    t10 = t("dashboard.affiliate_program.affiliate_email");
    $[35] = t;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = {
      key: "email",
      header: t10,
      widthPercent: 30,
      onRender: _temp
    };
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t) {
    t12 = t("dashboard.affiliate_program.sum_of_sales");
    $[39] = t;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== selectedCurrency || $[42] !== t || $[43] !== user?.dashboardLocale) {
    t13 = affiliate_0 => <div className="flex flex-col gap-1 truncate"><span className="truncate">{t("dashboard.affiliate_program.total_sales")}:{" "}{getAffiliateSum(affiliate_0.sums, selectedCurrency, user?.dashboardLocale)}</span><span className="truncate">{t("dashboard.affiliate_program.total_sales_after_refund")}:{" "}{getAffiliateSum(affiliate_0.sumsRefunded, selectedCurrency, user?.dashboardLocale)}</span></div>;
    $[41] = selectedCurrency;
    $[42] = t;
    $[43] = user?.dashboardLocale;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12 || $[46] !== t13) {
    t14 = {
      key: "sums",
      header: t12,
      widthPercent: 30,
      onRender: t13
    };
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== t) {
    t15 = t("dashboard.affiliate_program.total_commissions");
    $[48] = t;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== selectedCurrency || $[51] !== t || $[52] !== user?.dashboardLocale) {
    t16 = affiliate_1 => <div className="flex flex-col gap-1 truncate"><span className="truncate">{t("dashboard.affiliate_program.total_commissions")}:{" "}{getAffiliateSum(affiliate_1.commissions, selectedCurrency, user?.dashboardLocale)}</span><span className="truncate">{t("dashboard.affiliate_program.total_commissions_after_refund")}:{" "}{getAffiliateSum(affiliate_1.sumsRefundedCommissions, selectedCurrency, user?.dashboardLocale)}</span></div>;
    $[50] = selectedCurrency;
    $[51] = t;
    $[52] = user?.dashboardLocale;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t15 || $[55] !== t16) {
    t17 = {
      key: "commissions",
      header: t15,
      widthPercent: 30,
      onRender: t16
    };
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t) {
    t18 = t("dashboard.affiliate_program.num_of_leads_sent");
    $[57] = t;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t18) {
    t19 = {
      key: "leads",
      header: t18,
      widthPercent: 10
    };
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== t11 || $[62] !== t14 || $[63] !== t17 || $[64] !== t19) {
    t20 = [t11, t14, t17, t19];
    $[61] = t11;
    $[62] = t14;
    $[63] = t17;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const columnsAffiliates = t20;
  let t21;
  if ($[66] !== t) {
    t21 = t("dashboard.affiliate_program.offer_name");
    $[66] = t;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== t21) {
    t22 = {
      key: "id",
      header: t21,
      widthPercent: 60,
      onRender: _temp2
    };
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[70] !== t) {
    t23 = t("dashboard.affiliate_program.payout_delay");
    $[70] = t;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== isCommissionFetching || $[73] !== isPayoutFetching || $[74] !== onSavePayoutDelay) {
    t24 = commission_2 => <AffiliatePayoutInput isFetching={isPayoutFetching || isCommissionFetching} defaultValue={commission_2.payoutDelay} onSave={value_1 => onSavePayoutDelay(commission_2, value_1 || 0)} />;
    $[72] = isCommissionFetching;
    $[73] = isPayoutFetching;
    $[74] = onSavePayoutDelay;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== t23 || $[77] !== t24) {
    t25 = {
      key: "payoutDelay",
      header: t23,
      widthPercent: 20,
      onRender: t24
    };
    $[76] = t23;
    $[77] = t24;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] !== t) {
    t26 = t("dashboard.affiliate_program.affiliate_commission");
    $[79] = t;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== isCommissionFetching || $[82] !== isPayoutFetching || $[83] !== onSaveCommission) {
    t27 = commission_3 => <AffiliateCommissionInput isFetching={isCommissionFetching || isPayoutFetching} defaultValue={commission_3.affiliateCommission} onSave={value_2 => onSaveCommission(commission_3, value_2 || 0)} />;
    $[81] = isCommissionFetching;
    $[82] = isPayoutFetching;
    $[83] = onSaveCommission;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== t26 || $[86] !== t27) {
    t28 = {
      key: "affiliateCommission",
      header: t26,
      widthPercent: 20,
      onRender: t27
    };
    $[85] = t26;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== t22 || $[89] !== t25 || $[90] !== t28) {
    t29 = [t22, t25, t28];
    $[88] = t22;
    $[89] = t25;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  const columnsCommissions = t29;
  let t30;
  if ($[92] !== t) {
    t30 = t("dashboard.affiliate_program.title");
    $[92] = t;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t30) {
    t31 = <h1 className="flex h-[42px] items-center whitespace-nowrap">{t30}</h1>;
    $[94] = t30;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  const t32 = !user;
  let t33;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <SettingsIcon className="fill-background-secondary" />;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  let t34;
  if ($[97] !== t) {
    t34 = t("dashboard.affiliate_program.affiliate-program_settings");
    $[97] = t;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  let t35;
  if ($[99] !== t34) {
    t35 = <span className="hidden lg:flex">{t34}</span>;
    $[99] = t34;
    $[100] = t35;
  } else {
    t35 = $[100];
  }
  let t36;
  if ($[101] !== t32 || $[102] !== t35) {
    t36 = <PrimaryButton as={LinkWithoutPrefetch} href="/profile/affiliate-program-settings" className="flex justify-end xl:hidden" aria-disabled={t32}>{t33}{t35}</PrimaryButton>;
    $[101] = t32;
    $[102] = t35;
    $[103] = t36;
  } else {
    t36 = $[103];
  }
  const t37 = user?.timezone;
  let t38;
  if ($[104] !== t37) {
    t38 = getToday(t37);
    $[104] = t37;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  const t39 = !affiliateStatistic || !affiliates;
  let t40;
  if ($[106] !== user?.timezone) {
    t40 = () => getThisMonthDateRange(user?.timezone);
    $[106] = user?.timezone;
    $[107] = t40;
  } else {
    t40 = $[107];
  }
  let t41;
  if ($[108] !== affiliatesFilter.dateSince || $[109] !== affiliatesFilter.dateTill) {
    t41 = [affiliatesFilter.dateSince, affiliatesFilter.dateTill];
    $[108] = affiliatesFilter.dateSince;
    $[109] = affiliatesFilter.dateTill;
    $[110] = t41;
  } else {
    t41 = $[110];
  }
  let t42;
  if ($[111] !== setAffiliatesFilter) {
    t42 = date => setAffiliatesFilter(prev_0 => ({
      ...prev_0,
      dateSince: date[0],
      dateTill: date[1]
    }));
    $[111] = setAffiliatesFilter;
    $[112] = t42;
  } else {
    t42 = $[112];
  }
  const t43 = user?.timezone;
  const t44 = user?.createdAt;
  let t45;
  if ($[113] !== intlLocale || $[114] !== t38 || $[115] !== t39 || $[116] !== t40 || $[117] !== t41 || $[118] !== t42 || $[119] !== t43 || $[120] !== t44) {
    t45 = <DateRangePicker maxDate={t38} disabled={t39} getDefaultDate={t40} key="affiliate-date-filter" date={t41} onChange={t42} timezone={t43} createdAt={t44} intlLocale={intlLocale} />;
    $[113] = intlLocale;
    $[114] = t38;
    $[115] = t39;
    $[116] = t40;
    $[117] = t41;
    $[118] = t42;
    $[119] = t43;
    $[120] = t44;
    $[121] = t45;
  } else {
    t45 = $[121];
  }
  const t46 = affiliateStatistic && !selectedCurrency;
  let t47;
  if ($[122] !== t) {
    t47 = t("global.currency");
    $[122] = t;
    $[123] = t47;
  } else {
    t47 = $[123];
  }
  let t48;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = data_1 => setSelectedCurrency(data_1);
    $[124] = t48;
  } else {
    t48 = $[124];
  }
  let t49;
  if ($[125] !== currencyData || $[126] !== selectedCurrency || $[127] !== t46 || $[128] !== t47) {
    t49 = <FormSelect disabled={t46} className="max-w-[120px]" data={currencyData} placeholder={t47} withoutCloseIcon={true} value={selectedCurrency} onChange={t48} />;
    $[125] = currencyData;
    $[126] = selectedCurrency;
    $[127] = t46;
    $[128] = t47;
    $[129] = t49;
  } else {
    t49 = $[129];
  }
  const t50 = !user;
  let t51;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <SettingsIcon className="fill-background-secondary" />;
    $[130] = t51;
  } else {
    t51 = $[130];
  }
  let t52;
  if ($[131] !== t) {
    t52 = t("dashboard.affiliate_program.affiliate-program_settings");
    $[131] = t;
    $[132] = t52;
  } else {
    t52 = $[132];
  }
  let t53;
  if ($[133] !== t52) {
    t53 = <span className="hidden lg:flex">{t52}</span>;
    $[133] = t52;
    $[134] = t53;
  } else {
    t53 = $[134];
  }
  let t54;
  if ($[135] !== t50 || $[136] !== t53) {
    t54 = <PrimaryButton as={LinkWithoutPrefetch} href="/profile/affiliate-program-settings" aria-disabled={t50}>{t51}{t53}</PrimaryButton>;
    $[135] = t50;
    $[136] = t53;
    $[137] = t54;
  } else {
    t54 = $[137];
  }
  let t55;
  if ($[138] !== t45 || $[139] !== t49 || $[140] !== t54) {
    t55 = <div className="hidden gap-3 xl:flex">{t45}{t49}{t54}</div>;
    $[138] = t45;
    $[139] = t49;
    $[140] = t54;
    $[141] = t55;
  } else {
    t55 = $[141];
  }
  let t56;
  if ($[142] !== t36 || $[143] !== t55) {
    t56 = <div className="flex flex-col gap-3 xl:flex-row">{t36}{t55}</div>;
    $[142] = t36;
    $[143] = t55;
    $[144] = t56;
  } else {
    t56 = $[144];
  }
  let t57;
  if ($[145] !== t31 || $[146] !== t56) {
    t57 = <div className="flex w-full justify-between">{t31}{t56}</div>;
    $[145] = t31;
    $[146] = t56;
    $[147] = t57;
  } else {
    t57 = $[147];
  }
  const t58 = user?.timezone;
  let t59;
  if ($[148] !== t58) {
    t59 = getToday(t58);
    $[148] = t58;
    $[149] = t59;
  } else {
    t59 = $[149];
  }
  const t60 = !affiliateStatistic || !affiliates;
  let t61;
  if ($[150] !== user?.timezone) {
    t61 = () => getThisMonthDateRange(user?.timezone);
    $[150] = user?.timezone;
    $[151] = t61;
  } else {
    t61 = $[151];
  }
  let t62;
  if ($[152] !== affiliatesFilter.dateSince || $[153] !== affiliatesFilter.dateTill) {
    t62 = [affiliatesFilter.dateSince, affiliatesFilter.dateTill];
    $[152] = affiliatesFilter.dateSince;
    $[153] = affiliatesFilter.dateTill;
    $[154] = t62;
  } else {
    t62 = $[154];
  }
  let t63;
  if ($[155] !== setAffiliatesFilter) {
    t63 = date_0 => setAffiliatesFilter(prev_1 => ({
      ...prev_1,
      dateSince: date_0[0],
      dateTill: date_0[1]
    }));
    $[155] = setAffiliatesFilter;
    $[156] = t63;
  } else {
    t63 = $[156];
  }
  const t64 = user?.timezone;
  const t65 = user?.createdAt;
  let t66;
  if ($[157] !== intlLocale || $[158] !== t59 || $[159] !== t60 || $[160] !== t61 || $[161] !== t62 || $[162] !== t63 || $[163] !== t64 || $[164] !== t65) {
    t66 = <DateRangePicker maxDate={t59} disabled={t60} getDefaultDate={t61} key="affiliate-date-filter" date={t62} onChange={t63} timezone={t64} createdAt={t65} intlLocale={intlLocale} />;
    $[157] = intlLocale;
    $[158] = t59;
    $[159] = t60;
    $[160] = t61;
    $[161] = t62;
    $[162] = t63;
    $[163] = t64;
    $[164] = t65;
    $[165] = t66;
  } else {
    t66 = $[165];
  }
  const t67 = affiliateStatistic && !selectedCurrency;
  let t68;
  if ($[166] !== t) {
    t68 = t("global.currency");
    $[166] = t;
    $[167] = t68;
  } else {
    t68 = $[167];
  }
  let t69;
  if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = data_2 => setSelectedCurrency(data_2);
    $[168] = t69;
  } else {
    t69 = $[168];
  }
  let t70;
  if ($[169] !== currencyData || $[170] !== selectedCurrency || $[171] !== t67 || $[172] !== t68) {
    t70 = <FormSelect disabled={t67} className="max-w-[100px]" data={currencyData} placeholder={t68} withoutCloseIcon={true} value={selectedCurrency} onChange={t69} />;
    $[169] = currencyData;
    $[170] = selectedCurrency;
    $[171] = t67;
    $[172] = t68;
    $[173] = t70;
  } else {
    t70 = $[173];
  }
  let t71;
  if ($[174] !== t66 || $[175] !== t70) {
    t71 = <div className="flex flex-wrap justify-end gap-3 xl:hidden">{t66}{t70}</div>;
    $[174] = t66;
    $[175] = t70;
    $[176] = t71;
  } else {
    t71 = $[176];
  }
  let t72;
  if ($[177] !== t57 || $[178] !== t71) {
    t72 = <div className="flex flex-col justify-between gap-5 lg:gap-3 xl:flex-row">{t57}{t71}</div>;
    $[177] = t57;
    $[178] = t71;
    $[179] = t72;
  } else {
    t72 = $[179];
  }
  let t73;
  if ($[180] !== t) {
    t73 = t("dashboard.affiliate_program.total_sales");
    $[180] = t;
    $[181] = t73;
  } else {
    t73 = $[181];
  }
  let t74;
  if ($[182] !== t73) {
    t74 = <span className="font-inter text-text-primary flex w-1/3 justify-center text-sm font-bold sm:text-[17px]">{t73}</span>;
    $[182] = t73;
    $[183] = t74;
  } else {
    t74 = $[183];
  }
  let t75;
  if ($[184] !== t) {
    t75 = t("dashboard.affiliate_program.total_leads_sent_hint");
    $[184] = t;
    $[185] = t75;
  } else {
    t75 = $[185];
  }
  let t76;
  if ($[186] !== t) {
    t76 = t("dashboard.affiliate_program.total_leads_sent");
    $[186] = t;
    $[187] = t76;
  } else {
    t76 = $[187];
  }
  let t77;
  if ($[188] !== t76) {
    t77 = <span className="font-inter text-text-primary text-sm font-bold sm:text-[17px]">{t76}</span>;
    $[188] = t76;
    $[189] = t77;
  } else {
    t77 = $[189];
  }
  let t78;
  if ($[190] !== t75 || $[191] !== t77) {
    t78 = <Hint className="flex w-1/3 justify-center" label={t75}>{t77}</Hint>;
    $[190] = t75;
    $[191] = t77;
    $[192] = t78;
  } else {
    t78 = $[192];
  }
  let t79;
  if ($[193] !== t) {
    t79 = t("dashboard.affiliate_program.total_commissions");
    $[193] = t;
    $[194] = t79;
  } else {
    t79 = $[194];
  }
  let t80;
  if ($[195] !== t79) {
    t80 = <span className="font-inter text-text-primary flex w-1/3 justify-center text-sm font-bold sm:text-[17px]">{t79}</span>;
    $[195] = t79;
    $[196] = t80;
  } else {
    t80 = $[196];
  }
  let t81;
  if ($[197] !== t74 || $[198] !== t78 || $[199] !== t80) {
    t81 = <div className="bg-brand-primary-subtle flex rounded-t-lg px-6 py-4">{t74}{t78}{t80}</div>;
    $[197] = t74;
    $[198] = t78;
    $[199] = t80;
    $[200] = t81;
  } else {
    t81 = $[200];
  }
  let t82;
  if ($[201] !== affiliateStatistic || $[202] !== selectedCurrency || $[203] !== user?.dashboardLocale) {
    t82 = affiliateStatistic ? <div className="bg-background-secondary text-brand-primary flex rounded-b-lg px-6 py-4 text-sm font-bold sm:text-3xl"><span className="flex w-1/3 justify-center">{selectedCurrency ? currencyFormat(Array.isArray(affiliateStatistic.commonAffiliateSalesInfo) ? units(0) : centsToUnits(cents(affiliateStatistic.commonAffiliateSalesInfo[selectedCurrency].total_sum_after_refund)), selectedCurrency, user?.dashboardLocale) : currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)}</span><span className="flex w-1/3 justify-center">{affiliateStatistic.leadsCount}</span><span className="flex w-1/3 justify-center">{selectedCurrency ? currencyFormat(Array.isArray(affiliateStatistic.commonAffiliateSalesInfo) ? units(0) : centsToUnits(cents(affiliateStatistic.commonAffiliateSalesInfo[selectedCurrency].affiliate_commission_after_refund)), selectedCurrency, user?.dashboardLocale) : currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)}</span></div> : <AffiliateStatisticSkeleton />;
    $[201] = affiliateStatistic;
    $[202] = selectedCurrency;
    $[203] = user?.dashboardLocale;
    $[204] = t82;
  } else {
    t82 = $[204];
  }
  let t83;
  if ($[205] !== t81 || $[206] !== t82) {
    t83 = <div className="flex-col rounded-lg">{t81}{t82}</div>;
    $[205] = t81;
    $[206] = t82;
    $[207] = t83;
  } else {
    t83 = $[207];
  }
  let t84;
  if ($[208] !== t) {
    t84 = t("dashboard.affiliate_program.affiliates");
    $[208] = t;
    $[209] = t84;
  } else {
    t84 = $[209];
  }
  let t85;
  if ($[210] !== limitAffiliates || $[211] !== setLimitAffiliates) {
    t85 = {
      limit: limitAffiliates,
      setLimit: setLimitAffiliates
    };
    $[210] = limitAffiliates;
    $[211] = setLimitAffiliates;
    $[212] = t85;
  } else {
    t85 = $[212];
  }
  let t86;
  if ($[213] !== paginationAffiliates || $[214] !== setPaginationAffiliates) {
    t86 = {
      pagination: paginationAffiliates,
      setPagination: setPaginationAffiliates
    };
    $[213] = paginationAffiliates;
    $[214] = setPaginationAffiliates;
    $[215] = t86;
  } else {
    t86 = $[215];
  }
  let t87;
  if ($[216] !== affiliatesFilter || $[217] !== setAffiliatesFilter) {
    t87 = {
      defaultFilters,
      filter: affiliatesFilter,
      setFilter: setAffiliatesFilter
    };
    $[216] = affiliatesFilter;
    $[217] = setAffiliatesFilter;
    $[218] = t87;
  } else {
    t87 = $[218];
  }
  let t88;
  if ($[219] !== t) {
    t88 = {
      field: "email",
      onRender: (value_3, onChange) => <FormInputSearch key="affiliate-email-filter" value={value_3} placeholder={t("dashboard.affiliate_program.affiliate_email")} onChange={e_1 => onChange(e_1.target.value)} onClear={() => onChange("")} />
    };
    $[219] = t;
    $[220] = t88;
  } else {
    t88 = $[220];
  }
  let t89;
  if ($[221] !== t) {
    t89 = {
      field: "id",
      onRender: (value_4, onChange_0) => <FormInputSearch key="affiliate-id-filter" value={value_4} placeholder={t("dashboard.affiliate_program.affiliate_id")} onChange={e_2 => onChange_0(e_2.target.value)} onClear={() => onChange_0("")} />
    };
    $[221] = t;
    $[222] = t89;
  } else {
    t89 = $[222];
  }
  let t90;
  if ($[223] !== t88 || $[224] !== t89) {
    t90 = [t88, t89];
    $[223] = t88;
    $[224] = t89;
    $[225] = t90;
  } else {
    t90 = $[225];
  }
  let t91;
  if ($[226] !== t85 || $[227] !== t86 || $[228] !== t87 || $[229] !== t90) {
    t91 = {
      limiter: t85,
      paginator: t86,
      filterer: t87,
      filterable: t90
    };
    $[226] = t85;
    $[227] = t86;
    $[228] = t87;
    $[229] = t90;
    $[230] = t91;
  } else {
    t91 = $[230];
  }
  let t92;
  if ($[231] !== paginationAffiliates || $[232] !== setPaginationAffiliates) {
    t92 = {
      pagination: paginationAffiliates,
      setPagination: setPaginationAffiliates
    };
    $[231] = paginationAffiliates;
    $[232] = setPaginationAffiliates;
    $[233] = t92;
  } else {
    t92 = $[233];
  }
  let t93;
  if ($[234] !== limitAffiliates || $[235] !== setLimitAffiliates) {
    t93 = {
      limit: limitAffiliates,
      setLimit: setLimitAffiliates
    };
    $[234] = limitAffiliates;
    $[235] = setLimitAffiliates;
    $[236] = t93;
  } else {
    t93 = $[236];
  }
  let t94;
  if ($[237] !== affiliates || $[238] !== columnsAffiliates || $[239] !== t92 || $[240] !== t93) {
    t94 = <Table data={affiliates} columns={columnsAffiliates} paginator={t92} limiter={t93} />;
    $[237] = affiliates;
    $[238] = columnsAffiliates;
    $[239] = t92;
    $[240] = t93;
    $[241] = t94;
  } else {
    t94 = $[241];
  }
  let t95;
  if ($[242] !== affiliates || $[243] !== affiliatesCounter || $[244] !== t84 || $[245] !== t91 || $[246] !== t94) {
    t95 = <div><ListingLayout data={affiliates} counter={affiliatesCounter} title={t84} type="default" filterData={t91}>{t94}</ListingLayout></div>;
    $[242] = affiliates;
    $[243] = affiliatesCounter;
    $[244] = t84;
    $[245] = t91;
    $[246] = t94;
    $[247] = t95;
  } else {
    t95 = $[247];
  }
  let t96;
  if ($[248] !== t) {
    t96 = t("dashboard.affiliate_program.affiliate_commission_structure");
    $[248] = t;
    $[249] = t96;
  } else {
    t96 = $[249];
  }
  let t97;
  if ($[250] !== paginationCommissions || $[251] !== setPaginationCommissions) {
    t97 = {
      pagination: paginationCommissions,
      setPagination: setPaginationCommissions
    };
    $[250] = paginationCommissions;
    $[251] = setPaginationCommissions;
    $[252] = t97;
  } else {
    t97 = $[252];
  }
  let t98;
  if ($[253] !== limitCommissions || $[254] !== setLimitCommissions) {
    t98 = {
      limit: limitCommissions,
      setLimit: setLimitCommissions
    };
    $[253] = limitCommissions;
    $[254] = setLimitCommissions;
    $[255] = t98;
  } else {
    t98 = $[255];
  }
  let t99;
  if ($[256] !== columnsCommissions || $[257] !== commissions || $[258] !== t97 || $[259] !== t98) {
    t99 = <Table paginator={t97} limiter={t98} data={commissions} columns={columnsCommissions} />;
    $[256] = columnsCommissions;
    $[257] = commissions;
    $[258] = t97;
    $[259] = t98;
    $[260] = t99;
  } else {
    t99 = $[260];
  }
  let t100;
  if ($[261] !== commissions || $[262] !== commissionsCounter || $[263] !== t96 || $[264] !== t99) {
    t100 = <div><ListingLayout data={commissions} title={t96} type="compact" counter={commissionsCounter}>{t99}</ListingLayout></div>;
    $[261] = commissions;
    $[262] = commissionsCounter;
    $[263] = t96;
    $[264] = t99;
    $[265] = t100;
  } else {
    t100 = $[265];
  }
  let t101;
  if ($[266] !== t100 || $[267] !== t72 || $[268] !== t83 || $[269] !== t95) {
    t101 = <div className="flex flex-col gap-5">{t72}{t83}{t95}{t100}</div>;
    $[266] = t100;
    $[267] = t72;
    $[268] = t83;
    $[269] = t95;
    $[270] = t101;
  } else {
    t101 = $[270];
  }
  return t101;
};
export const getServerSideProps = getBaseServerSideProps(AffiliateProgramLoco, 'header.top_menu.affiliate_program');
export default AffiliateProgram;
function _temp(affiliate) {
  return <div className="truncate">{affiliate.userId ? <LinkWithoutPrefetch className="primary-link w-full" href={`/affiliate-program/${affiliate.affiliateId}`}>{affiliate.email}</LinkWithoutPrefetch> : <span className="w-full">{affiliate.email}</span>}</div>;
}
function _temp2(commission_1) {
  return <div className="truncate"><LinkWithoutPrefetch className="primary-link w-full" href={getFunnelStepUrl(commission_1.funnelStep.funnel.id, commission_1.funnelStep.id)}>{`${commission_1.funnelStep.name} (${commission_1.funnelStep.funnel.name})`}</LinkWithoutPrefetch></div>;
}